import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Progress } from '../components/ui/progress';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  ArrowLeft, Mail, Phone, MapPin, Calendar, Building2, GraduationCap,
  Briefcase, CreditCard, FileText, Clock, TrendingUp, Award,
  Plus, ArrowUpRight, ArrowRightLeft, AlertTriangle, Loader2, Trash2, BookOpen,
  IndianRupee, CheckCircle, XCircle, ArrowDown, ArrowUp
} from 'lucide-react';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { toast } from 'sonner';

import { API_BASE as API_URL } from '../lib/apiBase';
const getHeaders = () => {
  const token = localStorage.getItem('hrms-token');
  return token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : {};
};

const ENTRY_TYPE_CONFIG = {
  promotion:    { icon: ArrowUpRight, color: 'text-emerald-600', bg: 'bg-emerald-100 dark:bg-emerald-900/30', label: 'Promotion' },
  transfer:     { icon: ArrowRightLeft, color: 'text-blue-600', bg: 'bg-blue-100 dark:bg-blue-900/30', label: 'Transfer' },
  disciplinary: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-100 dark:bg-red-900/30', label: 'Disciplinary Action' },
};

const DISCIPLINARY_TYPES = ['Warning', 'Show Cause', 'Suspension', 'Fine', 'Demotion', 'Termination', 'Censure', 'Other'];

// ========== SERVICE BOOK TAB ==========
function ServiceBookTab({ employeeId, employee, entities }) {
  const { user } = useAuth();
  const canAdd = ['hr', 'admin', 'super_admin', 'hod'].includes(user?.role);
  const canDelete = ['hr', 'admin', 'super_admin'].includes(user?.role);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [departments, setDepartments] = useState([]);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/employees/${employeeId}/service-book`, { headers: getHeaders(), credentials: 'include' });
      if (res.ok) setEntries(await res.json());
    } catch { toast.error('Failed to load service book'); }
    finally { setLoading(false); }
  };

  const fetchDepartments = async () => {
    try {
      const res = await fetch(`${API_URL}/api/departments`, { headers: getHeaders(), credentials: 'include' });
      if (res.ok) setDepartments(await res.json());
    } catch {}
  };

  useEffect(() => { fetchEntries(); fetchDepartments(); }, [employeeId]);

  const handleDelete = async (entryId) => {
    if (!window.confirm('Delete this service book entry?')) return;
    try {
      const res = await fetch(`${API_URL}/api/service-book/${entryId}`, { method: 'DELETE', headers: getHeaders(), credentials: 'include' });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
      toast.success('Entry deleted');
      fetchEntries();
    } catch (err) { toast.error(err.message); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4" data-testid="service-book-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-[#DC2626]" /> Digital Service Book
        </h3>
        {canAdd && (
          <Button onClick={() => setDialogOpen(true)} data-testid="btn-add-service-entry">
            <Plus className="w-4 h-4 mr-1" /> Add Entry
          </Button>
        )}
      </div>

      {entries.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-400">
            <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No service book entries yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-0" data-testid="service-book-timeline">
          {entries.map((entry, i) => {
            const cfg = ENTRY_TYPE_CONFIG[entry.entry_type] || ENTRY_TYPE_CONFIG.promotion;
            const Icon = cfg.icon;
            return (
              <div key={entry.entry_id} className="flex gap-4" data-testid={`sb-entry-${entry.entry_id}`}>
                {/* Timeline line */}
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${cfg.bg}`}>
                    <Icon className={`w-5 h-5 ${cfg.color}`} />
                  </div>
                  {i < entries.length - 1 && <div className="w-0.5 flex-1 bg-slate-200 dark:bg-slate-700 my-1" />}
                </div>
                {/* Content */}
                <Card className="flex-1 mb-4 border-slate-200 dark:border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={`${cfg.bg} ${cfg.color} border-0 text-xs`}>{cfg.label}</Badge>
                          <span className="text-xs text-slate-500">{new Date(entry.effective_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        </div>
                        {/* Type-specific content */}
                        {entry.entry_type === 'promotion' && (
                          <div className="mt-2 space-y-1 text-sm">
                            <p><span className="text-slate-500">Designation:</span> <span className="line-through text-slate-400">{entry.old_designation}</span> <span className="mx-1">→</span> <span className="font-semibold text-emerald-700 dark:text-emerald-400">{entry.new_designation}</span></p>
                            {(entry.old_pay_scale || entry.new_pay_scale) && <p><span className="text-slate-500">Pay Scale:</span> {entry.old_pay_scale} → {entry.new_pay_scale}</p>}
                            {(entry.old_basic_salary > 0 || entry.new_basic_salary > 0) && <p><span className="text-slate-500">Salary:</span> ₹{(entry.old_basic_salary || 0).toLocaleString()} → ₹{(entry.new_basic_salary || 0).toLocaleString()}</p>}
                          </div>
                        )}
                        {entry.entry_type === 'transfer' && (
                          <div className="mt-2 space-y-1 text-sm">
                            <p><span className="text-slate-500">From:</span> {entry.from_entity_name || entry.from_entity_id} — {entry.from_department}</p>
                            <p><span className="text-slate-500">To:</span> <span className="font-semibold text-blue-700 dark:text-blue-400">{entry.to_entity_name || entry.to_entity_id} — {entry.to_department}</span></p>
                            {entry.transfer_reason && <p><span className="text-slate-500">Reason:</span> {entry.transfer_reason}</p>}
                          </div>
                        )}
                        {entry.entry_type === 'disciplinary' && (
                          <div className="mt-2 space-y-1 text-sm">
                            <p><span className="text-slate-500">Action:</span> <span className="font-semibold text-red-700 dark:text-red-400">{entry.action_type}</span></p>
                            {entry.description && <p><span className="text-slate-500">Details:</span> {entry.description}</p>}
                            {entry.outcome && <p><span className="text-slate-500">Outcome:</span> {entry.outcome}</p>}
                          </div>
                        )}
                        {entry.order_reference && <p className="text-xs text-slate-500 mt-2">Ref: {entry.order_reference}</p>}
                        {entry.remarks && <p className="text-xs text-slate-500 italic mt-1">"{entry.remarks}"</p>}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <span className="text-xs text-slate-400">by {entry.created_by_name}</span>
                        {canDelete && (
                          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 h-7 w-7 p-0" onClick={() => handleDelete(entry.entry_id)} data-testid={`btn-delete-${entry.entry_id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      )}

      <AddServiceEntryDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={fetchEntries}
        employeeId={employeeId}
        employee={employee}
        entities={entities || []}
        departments={departments}
      />
    </div>
  );
}

// ========== ADD ENTRY DIALOG ==========
function AddServiceEntryDialog({ open, onClose, onSaved, employeeId, employee, entities, departments }) {
  const [entryType, setEntryType] = useState('promotion');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    effective_date: new Date().toISOString().slice(0, 10),
    order_reference: '', remarks: '',
    old_designation: '', new_designation: '', old_pay_scale: '', new_pay_scale: '',
    old_basic_salary: 0, new_basic_salary: 0,
    from_entity_id: '', from_department: '', to_entity_id: '', to_department: '', transfer_reason: '',
    action_type: 'Warning', description: '', outcome: ''
  });

  useEffect(() => {
    if (open && employee) {
      setForm(f => ({
        ...f,
        old_designation: employee.designation || '',
        old_basic_salary: employee.basic_salary || 0,
        from_entity_id: employee.entity_id || '',
        from_department: employee.department || '',
      }));
    }
  }, [open, employee]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.effective_date) { toast.error('Effective date is required'); return; }
    setSaving(true);
    try {
      const params = new URLSearchParams({ entry_type: entryType, effective_date: form.effective_date });
      if (form.order_reference) params.set('order_reference', form.order_reference);
      if (form.remarks) params.set('remarks', form.remarks);

      if (entryType === 'promotion') {
        params.set('old_designation', form.old_designation);
        params.set('new_designation', form.new_designation);
        params.set('old_pay_scale', form.old_pay_scale);
        params.set('new_pay_scale', form.new_pay_scale);
        params.set('old_basic_salary', form.old_basic_salary);
        params.set('new_basic_salary', form.new_basic_salary);
      } else if (entryType === 'transfer') {
        params.set('from_entity_id', form.from_entity_id);
        params.set('from_department', form.from_department);
        params.set('to_entity_id', form.to_entity_id);
        params.set('to_department', form.to_department);
        params.set('transfer_reason', form.transfer_reason);
      } else if (entryType === 'disciplinary') {
        params.set('action_type', form.action_type);
        params.set('description', form.description);
        params.set('outcome', form.outcome);
      }

      const res = await fetch(`${API_URL}/api/employees/${employeeId}/service-book?${params}`, {
        method: 'POST', headers: getHeaders(), credentials: 'include'
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Failed'); }
      toast.success('Service book entry added');
      onSaved();
      onClose();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const filteredFromDepts = departments.filter(d => !form.from_entity_id || d.entity_id === form.from_entity_id);
  const filteredToDepts = departments.filter(d => !form.to_entity_id || d.entity_id === form.to_entity_id);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-service-entry-dialog">
        <DialogHeader>
          <DialogTitle>Add Service Book Entry</DialogTitle>
          <DialogDescription>Record a promotion, transfer, or disciplinary action.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Entry Type</Label>
              <Select value={entryType} onValueChange={setEntryType}>
                <SelectTrigger data-testid="select-entry-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="promotion">Promotion</SelectItem>
                  <SelectItem value="transfer">Transfer</SelectItem>
                  <SelectItem value="disciplinary">Disciplinary Action</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Effective Date *</Label>
              <Input data-testid="input-effective-date" type="date" value={form.effective_date} onChange={e => set('effective_date', e.target.value)} />
            </div>
          </div>

          {/* Promotion fields */}
          {entryType === 'promotion' && (
            <div className="space-y-3 border-t pt-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Old Designation</Label>
                  <Input data-testid="input-old-designation" value={form.old_designation} onChange={e => set('old_designation', e.target.value)} />
                </div>
                <div>
                  <Label>New Designation *</Label>
                  <Input data-testid="input-new-designation" value={form.new_designation} onChange={e => set('new_designation', e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Old Pay Scale</Label>
                  <Input data-testid="input-old-pay-scale" value={form.old_pay_scale} onChange={e => set('old_pay_scale', e.target.value)} />
                </div>
                <div>
                  <Label>New Pay Scale</Label>
                  <Input data-testid="input-new-pay-scale" value={form.new_pay_scale} onChange={e => set('new_pay_scale', e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Old Basic Salary</Label>
                  <Input data-testid="input-old-salary" type="number" value={form.old_basic_salary} onChange={e => set('old_basic_salary', parseFloat(e.target.value) || 0)} />
                </div>
                <div>
                  <Label>New Basic Salary</Label>
                  <Input data-testid="input-new-salary" type="number" value={form.new_basic_salary} onChange={e => set('new_basic_salary', parseFloat(e.target.value) || 0)} />
                </div>
              </div>
            </div>
          )}

          {/* Transfer fields */}
          {entryType === 'transfer' && (
            <div className="space-y-3 border-t pt-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>From Institute</Label>
                  <Select value={form.from_entity_id} onValueChange={v => set('from_entity_id', v)}>
                    <SelectTrigger data-testid="select-from-entity"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {entities.map(e => <SelectItem key={e.entity_id} value={e.entity_id}>{e.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>From Department</Label>
                  <Select value={form.from_department} onValueChange={v => set('from_department', v)}>
                    <SelectTrigger data-testid="select-from-dept"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {filteredFromDepts.map(d => <SelectItem key={d.department_id} value={d.name}>{d.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>To Institute</Label>
                  <Select value={form.to_entity_id} onValueChange={v => set('to_entity_id', v)}>
                    <SelectTrigger data-testid="select-to-entity"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {entities.map(e => <SelectItem key={e.entity_id} value={e.entity_id}>{e.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>To Department</Label>
                  <Select value={form.to_department} onValueChange={v => set('to_department', v)}>
                    <SelectTrigger data-testid="select-to-dept"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {filteredToDepts.map(d => <SelectItem key={d.department_id} value={d.name}>{d.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Transfer Reason</Label>
                <Textarea data-testid="input-transfer-reason" value={form.transfer_reason} onChange={e => set('transfer_reason', e.target.value)} rows={2} />
              </div>
            </div>
          )}

          {/* Disciplinary fields */}
          {entryType === 'disciplinary' && (
            <div className="space-y-3 border-t pt-3">
              <div>
                <Label>Action Type</Label>
                <Select value={form.action_type} onValueChange={v => set('action_type', v)}>
                  <SelectTrigger data-testid="select-action-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DISCIPLINARY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea data-testid="input-disciplinary-desc" value={form.description} onChange={e => set('description', e.target.value)} rows={3} placeholder="Details of the disciplinary action..." />
              </div>
              <div>
                <Label>Outcome</Label>
                <Input data-testid="input-outcome" value={form.outcome} onChange={e => set('outcome', e.target.value)} placeholder="e.g., Warning issued, Suspended for 7 days" />
              </div>
            </div>
          )}

          {/* Common fields */}
          <div className="grid grid-cols-2 gap-3 border-t pt-3">
            <div>
              <Label>Order/Letter Reference</Label>
              <Input data-testid="input-order-ref" value={form.order_reference} onChange={e => set('order_reference', e.target.value)} placeholder="e.g., Office Order #123" />
            </div>
            <div>
              <Label>Remarks</Label>
              <Input data-testid="input-remarks" value={form.remarks} onChange={e => set('remarks', e.target.value)} />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="btn-save-service-entry">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Add Entry
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const SALARY_LABELS = {
  basic_salary: 'Basic Salary', gross_salary: 'Gross Salary', agp: 'AGP',
  da: 'DA', hra: 'HRA', cla: 'CLA', other_allowance: 'Other Allowance',
  provident_fund: 'Provident Fund', profession_tax: 'Profession Tax',
  bus: 'Bus', accommodation: 'Accommodation', tds: 'TDS',
  localmart: 'LocalMart', other_deduction: 'Other Deduction',
  pay_scale: 'Pay Scale', designation: 'Designation',
};

const fmt = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);

// ========== SALARY HISTORY TAB ==========
function SalaryHistoryTab({ employeeId }) {
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/salary-revisions?employee_id=${employeeId}`, {
          headers: getHeaders(), credentials: 'include'
        });
        if (res.ok) setRevisions(await res.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [employeeId]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  // Compute overall increment summary from approved revisions
  const approved = revisions.filter(r => r.status === 'approved');
  const totalBasicBefore = approved.length > 0 ? approved[approved.length - 1]?.current_salary?.basic_salary || 0 : 0;
  const totalBasicAfter = approved.length > 0 ? approved[0]?.proposed_changes?.basic_salary || totalBasicBefore : 0;
  const overallPct = totalBasicBefore > 0 ? (((totalBasicAfter - totalBasicBefore) / totalBasicBefore) * 100) : 0;

  const STATUS_CFG = {
    pending: { color: 'bg-amber-100 text-amber-700', icon: Clock, label: 'Pending' },
    approved: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Approved' },
    rejected: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Rejected' },
  };

  return (
    <div className="space-y-5" data-testid="salary-history-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
          <IndianRupee className="w-5 h-5 text-[#DC2626]" /> Salary Revision History
        </h3>
        {approved.length > 0 && (
          <Badge className="bg-blue-100 text-blue-700 text-xs">
            {approved.length} revision{approved.length !== 1 ? 's' : ''} applied
          </Badge>
        )}
      </div>

      {/* Overall Summary Card */}
      {approved.length > 0 && (
        <Card className="border border-slate-200 bg-gradient-to-r from-slate-50 to-white dark:from-slate-800 dark:to-slate-900">
          <CardContent className="p-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">First Recorded Basic</p>
                <p className="text-lg font-bold font-mono mt-1">{fmt(totalBasicBefore)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Latest Basic</p>
                <p className="text-lg font-bold font-mono mt-1 text-[#DC2626]">{fmt(totalBasicAfter)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Overall Change</p>
                <p className={`text-lg font-bold font-mono mt-1 flex items-center justify-center gap-1 ${overallPct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {overallPct >= 0 ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                  {Math.abs(overallPct).toFixed(1)}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {revisions.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-400">
            <IndianRupee className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No salary revisions recorded for this employee</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-0" data-testid="salary-history-timeline">
          {revisions.map((rev, i) => {
            const cfg = STATUS_CFG[rev.status] || STATUS_CFG.pending;
            const Icon = cfg.icon;
            const changes = Object.entries(rev.proposed_changes || {});

            return (
              <div key={rev.revision_id} className="flex gap-4" data-testid={`sal-rev-${rev.revision_id}`}>
                {/* Timeline connector */}
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    rev.status === 'approved' ? 'bg-green-100 dark:bg-green-900/30' :
                    rev.status === 'rejected' ? 'bg-red-100 dark:bg-red-900/30' :
                    'bg-amber-100 dark:bg-amber-900/30'
                  }`}>
                    <Icon className={`w-5 h-5 ${
                      rev.status === 'approved' ? 'text-green-600' :
                      rev.status === 'rejected' ? 'text-red-600' : 'text-amber-600'
                    }`} />
                  </div>
                  {i < revisions.length - 1 && <div className="w-0.5 flex-1 bg-slate-200 dark:bg-slate-700 my-1" />}
                </div>

                {/* Card content */}
                <Card className="flex-1 mb-4 border-slate-200 dark:border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge className={`${cfg.color} border-0 text-xs`}>{cfg.label}</Badge>
                          <span className="text-xs text-slate-500 font-mono">{rev.effective_date}</span>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">{rev.reason}</p>
                      </div>
                      <span className="text-[11px] text-slate-400 whitespace-nowrap">
                        by {rev.proposed_by_email?.split('@')[0]}
                      </span>
                    </div>

                    {/* Component Changes Table */}
                    <div className="mt-3 rounded overflow-hidden border border-slate-100 dark:border-slate-700">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50 dark:bg-slate-800">
                            <TableHead className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 py-2">Component</TableHead>
                            <TableHead className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 py-2 text-right">Before</TableHead>
                            <TableHead className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 py-2 text-right">After</TableHead>
                            <TableHead className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 py-2 text-right">Change</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {changes.map(([k, v]) => {
                            const cur = rev.current_salary?.[k];
                            const isNum = typeof v === 'number' && typeof cur === 'number';
                            const diff = isNum ? v - cur : null;
                            const pct = isNum && cur > 0 ? ((diff / cur) * 100) : null;
                            return (
                              <TableRow key={k} className="border-b border-slate-50 dark:border-slate-800">
                                <TableCell className="text-sm py-1.5">{SALARY_LABELS[k] || k}</TableCell>
                                <TableCell className="text-sm py-1.5 text-right font-mono text-slate-500">{isNum ? fmt(cur) : (cur || '-')}</TableCell>
                                <TableCell className="text-sm py-1.5 text-right font-mono font-semibold">{isNum ? fmt(v) : v}</TableCell>
                                <TableCell className="text-sm py-1.5 text-right font-mono">
                                  {diff !== null && (
                                    <span className={`inline-flex items-center gap-0.5 ${diff > 0 ? 'text-green-600' : diff < 0 ? 'text-red-600' : 'text-slate-400'}`}>
                                      {diff > 0 ? <ArrowUp className="w-3 h-3" /> : diff < 0 ? <ArrowDown className="w-3 h-3" /> : null}
                                      {pct !== null ? `${Math.abs(pct).toFixed(1)}%` : fmt(Math.abs(diff))}
                                    </span>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Approval info */}
                    {rev.approval_log?.length > 0 && (
                      <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
                        <span>{rev.approval_log[0].action === 'approve' ? 'Approved' : 'Rejected'} by {rev.approval_log[0].email}</span>
                        {rev.approval_log[0].comment && <span>— "{rev.approval_log[0].comment}"</span>}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ========== MAIN EMPLOYEE DETAIL ==========
export default function EmployeeDetail() {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [employee, setEmployee] = useState(null);
  const [leaveBalance, setLeaveBalance] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEmployeeData();
  }, [employeeId]);

  const fetchEmployeeData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('hrms-token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      const [empRes, leaveRes, attRes, entRes] = await Promise.all([
        fetch(`${API_URL}/api/employees/${employeeId}`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/leaves/balance/${employeeId}`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/attendance?employee_id=${employeeId}`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/entities`, { headers, credentials: 'include' })
      ]);

      if (empRes.ok) setEmployee(await empRes.json());
      else throw new Error('Employee not found');
      
      if (leaveRes.ok) setLeaveBalance(await leaveRes.json());
      if (attRes.ok) setAttendance(await attRes.json());
      if (entRes.ok) setEntities(await entRes.json());
    } catch (error) {
      toast.error('Failed to load employee data');
      navigate('/employees');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-48 bg-slate-200 dark:bg-slate-700 rounded-sm" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="h-64 bg-slate-200 dark:bg-slate-700 rounded-sm" />
          <div className="h-64 bg-slate-200 dark:bg-slate-700 rounded-sm md:col-span-2" />
        </div>
      </div>
    );
  }

  if (!employee) return null;

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  const canViewSalaryHistory = ['finance', 'hr', 'admin', 'super_admin', 'principal'].includes(user?.role);

  const recentAttendance = attendance.slice(0, 10);
  const presentDays = attendance.filter(a => a.status === 'present').length;
  const attendanceRate = attendance.length > 0 ? (presentDays / attendance.length * 100) : 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="employee-detail-page">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate('/employees')}
        className="mb-4"
        data-testid="back-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Employees
      </Button>

      {/* Profile Header */}
      <Card className="overflow-hidden">
        <div className="h-32 bg-gradient-to-r from-slate-900 to-slate-700" />
        <CardContent className="relative pb-6">
          <div className="flex flex-col md:flex-row gap-6">
            <Avatar className="-mt-16 md:-mt-14 w-24 h-24 md:w-32 md:h-32 border-4 border-white dark:border-slate-800 shadow-lg bg-white">
              <AvatarImage src={employee.profile_picture} />
              <AvatarFallback className="bg-[#DC2626] text-white text-2xl">
                {getInitials(employee.first_name, employee.last_name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 pt-2">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                    {employee.first_name} {employee.last_name}
                  </h1>
                  <p className="text-slate-500">{employee.designation}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge className="bg-[#DC2626] text-white">{employee.category}</Badge>
                    <Badge variant="outline">{employee.department}</Badge>
                    <Badge className={employee.is_active ? 'badge-success' : 'badge-error'}>
                      {employee.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" data-testid="edit-employee-btn">
                    Edit Profile
                  </Button>
                  <Button className="btn-primary" data-testid="generate-payslip-btn">
                    Generate Payslip
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Details Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="service-book" data-testid="tab-service-book">Service Book</TabsTrigger>
          <TabsTrigger value="salary-history" data-testid="tab-salary-history">Salary History</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-attendance">Attendance</TabsTrigger>
          <TabsTrigger value="leaves" data-testid="tab-leaves">Leave Balance</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Personal Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Personal Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Email</p>
                    <p className="font-medium">{employee.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Phone</p>
                    <p className="font-medium">{employee.phone || 'Not provided'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Address</p>
                    <p className="font-medium">{employee.address || 'Not provided'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Date of Birth</p>
                    <p className="font-medium">{employee.date_of_birth || 'Not provided'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Employment Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Employment Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <Briefcase className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Employee ID</p>
                    <code className="font-mono text-sm bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">
                      {employee.employee_id}
                    </code>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Date of Joining</p>
                    <p className="font-medium">{employee.date_of_joining}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Building2 className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Appointment Type</p>
                    <p className="font-medium">{employee.appointment_type}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <GraduationCap className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Qualification</p>
                    <p className="font-medium">{employee.highest_qualification || 'Not specified'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Experience</p>
                    <p className="font-medium">{employee.experience_years} years</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Salary & Compliance */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Salary & Compliance</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <CreditCard className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Basic Salary</p>
                    <p className="font-medium text-lg">₹{employee.basic_salary?.toLocaleString() || 0}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <FileText className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Pay Scale</p>
                    <p className="font-medium">{employee.pay_scale || 'Standard'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Award className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">API Score</p>
                    <p className="font-medium">{employee.api_score || 0}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-slate-500">UGC Approval</p>
                  <Badge className={employee.ugc_approval_status === 'approved' ? 'badge-success' : 'badge-warning'}>
                    {employee.ugc_approval_status || 'Pending'}
                  </Badge>
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-slate-500">AICTE Approval</p>
                  <Badge className={employee.aicte_approval_status === 'approved' ? 'badge-success' : 'badge-warning'}>
                    {employee.aicte_approval_status || 'Pending'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Attendance Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#DC2626]" />
                Attendance Summary (Last 30 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-8">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-500">Attendance Rate</span>
                    <span className="font-bold text-lg">{attendanceRate.toFixed(1)}%</span>
                  </div>
                  <Progress value={attendanceRate} className="h-3" />
                </div>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-green-600">{presentDays}</p>
                    <p className="text-xs text-slate-500">Present</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-600">
                      {attendance.filter(a => a.status === 'absent').length}
                    </p>
                    <p className="text-xs text-slate-500">Absent</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-yellow-600">
                      {attendance.filter(a => a.status === 'on_leave').length}
                    </p>
                    <p className="text-xs text-slate-500">On Leave</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Service Book Tab */}
        <TabsContent value="service-book">
          <ServiceBookTab employeeId={employeeId} employee={employee} entities={entities} />
        </TabsContent>

        {/* Salary History Tab */}
        <TabsContent value="salary-history">
          {canViewSalaryHistory ? (
            <SalaryHistoryTab employeeId={employeeId} />
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-slate-400">
                <IndianRupee className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="text-sm">You do not have permission to view salary revision history</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="attendance">
          <Card>
            <CardHeader>
              <CardTitle>Recent Attendance Records</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {recentAttendance.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No attendance records found</p>
                ) : (
                  recentAttendance.map(att => (
                    <div 
                      key={att.attendance_id}
                      className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded"
                    >
                      <div className="flex items-center gap-4">
                        <div className="text-sm">
                          <p className="font-medium">{new Date(att.date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}</p>
                          <p className="text-slate-500">
                            {att.check_in && att.check_out ? `${att.check_in} - ${att.check_out}` : 'N/A'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-slate-500">{att.work_hours?.toFixed(1) || 0}h</span>
                        <Badge className={
                          att.status === 'present' ? 'badge-success' :
                          att.status === 'absent' ? 'badge-error' : 'badge-warning'
                        }>
                          {att.status}
                        </Badge>
                        {att.late_mark && <Badge variant="outline" className="text-orange-600">Late</Badge>}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="leaves">
          <Card>
            <CardHeader>
              <CardTitle>Leave Balance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {leaveBalance.map(leave => (
                  <div key={leave.leave_type} className="p-4 bg-slate-50 dark:bg-slate-800 rounded">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{leave.leave_type}</span>
                      <Badge variant="outline">{leave.balance}/{leave.total}</Badge>
                    </div>
                    <Progress value={(leave.balance / leave.total) * 100} className="h-2" />
                    <p className="text-xs text-slate-500 mt-1">{leave.used} days used</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documents">
          <Card>
            <CardHeader>
              <CardTitle>Identity Documents</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 border rounded">
                  <p className="text-sm text-slate-500">Aadhaar Number</p>
                  <p className="font-mono text-lg">{employee.aadhaar_number || '••••••••••••'}</p>
                </div>
                <div className="p-4 border rounded">
                  <p className="text-sm text-slate-500">PAN Number</p>
                  <p className="font-mono text-lg">{employee.pan_number || '••••••••••'}</p>
                </div>
                <div className="p-4 border rounded">
                  <p className="text-sm text-slate-500">Bank Account</p>
                  <p className="font-mono text-lg">{employee.bank_account || '••••••••••••'}</p>
                </div>
                <div className="p-4 border rounded">
                  <p className="text-sm text-slate-500">IFSC Code</p>
                  <p className="font-mono text-lg">{employee.ifsc_code || '••••••••••••'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
